import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, MapPin, Users, Car, Shield, Clock, Plane, Star, Phone } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const vehicleTypeIcons = {
  'automóvil': Car,
  'suv': Car,
  'van': Car,
  'sprinter': Car,
  'minibús': Car,
  'autobús': Car,
  'blindado': Shield,
  'default': Car
};

export default function TransportPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');


  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/public/transport?limit=50`);
        if (response.data.items?.length > 0) {
          setServices(response.data.items);
        } else {
          setServices([]);
        }
      } catch (error) {
        console.error('Error fetching transport services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const vehicleTypes = ['all', 'SUV', 'Van', 'Sprinter', 'Autobús', 'Lujo', 'Blindado'];
  const serviceLevels = ['all', 'básico', 'ejecutivo', 'premium', 'lujo', 'VIP', 'blindado'];

  const filteredServices = services.filter(service => {
    const matchesType = filter === 'all' || service.vehicle_types?.some(t => t.toLowerCase().includes(filter.toLowerCase()));
    const matchesLevel = levelFilter === 'all' || service.service_levels?.some(l => l.toLowerCase() === levelFilter.toLowerCase());
    const matchesDriver = driverFilter === 'all' || 
      (driverFilter === 'with' && service.with_driver) ||
      (driverFilter === 'without' && service.without_driver);
    const matchesSearch = searchTerm === '' || 
      (service.business_name?.[lang] || service.business_name?.es || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.city?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesLevel && matchesDriver && matchesSearch;
  });

  const getVehicleIcon = (types) => {
    if (!types || types.length === 0) return Car;
    const type = types[0].toLowerCase();
    for (const [key, Icon] of Object.entries(vehicleTypeIcons)) {
      if (type.includes(key)) return Icon;
    }
    return Car;
  };

  const formatPrice = (pricing) => {
    if (!pricing) return 'Cotizar';
    if (pricing.quote_only) return 'Cotizar';
    if (pricing.price_per_hour) return `$${pricing.price_per_hour.toLocaleString()}/hr`;
    if (pricing.price_per_day) return `$${pricing.price_per_day.toLocaleString()}/día`;
    if (pricing.price_per_event) return `$${pricing.price_per_event.toLocaleString()}/evento`;
    if (pricing.base_price) return `Desde $${pricing.base_price.toLocaleString()}`;
    return 'Cotizar';
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16" data-testid="transport-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] to-[#050505]" />
        <div className="container-premium relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <img 
                src="/gs-agency-logo-gold.png" 
                alt="GS Agency" 
                className="h-48 md:h-64 w-auto object-contain"
              />
            </motion.div>
            <motion.p variants={fadeInUp} className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
              Servicios de Transporte Premium
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl text-white mb-4">
              Transportación
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-white/60 text-lg max-w-2xl mb-8">
              Soluciones de transporte ejecutivo y de lujo para eventos, artistas, clientes VIP y producción
            </motion.p>

            {/* Filters */}
            <motion.div variants={fadeInUp} className="space-y-4">
              {/* Vehicle Type Filter */}
              <div className="flex flex-wrap gap-2">
                {vehicleTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setFilter(type)}
                    className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                      filter === type
                        ? 'bg-white text-black'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`filter-${type}`}
                  >
                    {type === 'all' ? 'Todos' : type}
                  </button>
                ))}
              </div>
              
              {/* Service Level & Driver Filters */}
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                <div className="flex gap-2">
                  <select
                    value={levelFilter}
                    onChange={(e) => setLevelFilter(e.target.value)}
                    className="input-premium px-4 py-2.5 bg-[#0a0a0a] text-white border border-white/10 rounded-lg"
                    data-testid="level-filter"
                  >
                    <option value="all">Nivel de Servicio</option>
                    {serviceLevels.slice(1).map(level => (
                      <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>
                    ))}
                  </select>
                  
                  <select
                    value={driverFilter}
                    onChange={(e) => setDriverFilter(e.target.value)}
                    className="input-premium px-4 py-2.5 bg-[#0a0a0a] text-white border border-white/10 rounded-lg"
                    data-testid="driver-filter"
                  >
                    <option value="all">Chofer</option>
                    <option value="with">Con chofer</option>
                    <option value="without">Sin chofer</option>
                  </select>
                </div>

                <div className="relative w-full md:w-auto">
                  <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre o ciudad..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-premium pl-12 pr-4 py-3 w-full md:w-64"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-dark py-16" data-testid="transport-grid">
        <div className="container-premium">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/3] loading-skeleton rounded-lg" />
              ))}
            </div>
          ) : filteredServices.length > 0 ? (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredServices.map((service) => {
                const VehicleIcon = getVehicleIcon(service.vehicle_types);
                return (
                  <motion.div key={service.transport_id || service.slug} variants={fadeInUp}>
                    <Link to={`/transport/${service.slug}`} className="block group" data-testid={`transport-${service.slug}`}>
                      <div className="premium-card overflow-hidden">
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={service.cover_image || 'https://images.pexels.com/photos/1035108/pexels-photo-1035108.jpeg?auto=compress&cs=tinysrgb&w=800'}
                            alt={service.business_name?.[lang] || service.business_name?.es}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          {service.featured && (
                            <div className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-black text-xs font-medium rounded-full flex items-center gap-1">
                              <Star size={12} /> Destacado
                            </div>
                          )}
                          {service.airport_service && (
                            <div className="absolute top-4 right-4 p-2 bg-black/50 rounded-full">
                              <Plane size={16} className="text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {service.vehicle_types?.slice(0, 2).map((type) => (
                              <span key={type} className="text-[#D4AF37] text-xs tracking-wider uppercase">
                                {type}
                              </span>
                            ))}
                            {service.service_levels?.slice(0, 1).map((level) => (
                              <span key={level} className="text-[#C0C0C0] text-xs tracking-wider uppercase border border-[#C0C0C0]/30 px-2 py-0.5 rounded">
                                {level}
                              </span>
                            ))}
                          </div>
                          <h3 className="font-display text-xl text-white mb-2">
                            {service.business_name?.[lang] || service.business_name?.es}
                          </h3>
                          <div className="flex items-center justify-between text-white/50 text-sm mb-3">
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {service.city}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users size={14} />
                              {service.passenger_capacity} pax
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[#D4AF37] font-semibold">
                              {formatPrice(service.pricing)}
                            </span>
                            {service.with_driver && (
                              <span className="text-white/40 text-xs flex items-center gap-1">
                                <Clock size={12} /> Con chofer
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <div className="text-center py-20">
              <Car size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/50">No se encontraron servicios de transporte</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-subtle" data-testid="transport-cta">
        <div className="container-premium text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeInUp} className="font-display text-3xl md:text-4xl text-white mb-4">
              ¿Ofreces servicios de transporte?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/60 mb-8 max-w-xl mx-auto">
              Únete a nuestra red de proveedores premium y conecta con clientes de alto nivel
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-semibold rounded-lg hover:bg-[#B8962E] transition-colors"
                data-testid="register-provider-btn"
              >
                <Car size={20} />
                Registrar mi servicio
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 section-dark border-t border-white/5">
        <div className="container-premium">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-white text-xl mb-2">¿Necesitas transporte para un evento?</h3>
              <p className="text-white/60">Contáctanos para una cotización personalizada</p>
            </div>
            <Link
              to="/contact"
              className="flex items-center gap-2 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] rounded-lg hover:bg-[#D4AF37] hover:text-black transition-colors"
              data-testid="contact-btn"
            >
              <Phone size={18} />
              Solicitar cotización
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
